// Login page component - Clean template design
import { router } from '@/utils/router'
import { api } from '@/services/api'

export class LoginPage {
  private container: HTMLElement | null = null
  private nif: string = ''
  private password: string = ''

  async render(): Promise<HTMLElement> {
    this.container = document.createElement('div')
    this.container.innerHTML = `
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .login-body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          background-color: #f5f5f5;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 20px;
        }

        .login-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          padding: 40px;
          width: 100%;
          max-width: 420px;
        }

        .header {
          text-align: center;
          margin-bottom: 32px;
        }

        .header h1 {
          font-size: 28px;
          font-weight: bold !important;
          color: #000000 !important;
          margin-bottom: 8px;
        }

        .header p {
          color: #666;
          font-size: 16px;
          line-height: 1.4;
        }

        .tab-container {
          display: flex;
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 4px;
          margin-bottom: 32px;
        }

        .tab {
          flex: 1;
          padding: 12px 20px;
          text-align: center;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          border: none;
          background: none;
        }

        .tab.active {
          background-color: white;
          color: #1a1a1a;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .tab:not(.active) {
          color: #666;
        }

        .tab:not(.active):hover {
          color: #1a1a1a;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-group label {
          display: block;
          font-weight: 500;
          color: #1a1a1a;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .form-group input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          background-color: white;
        }

        .form-group input::placeholder {
          color: #999;
        }

        .form-group input:focus {
          outline: none;
          border-color: #4285f4;
          box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
        }

        .login-btn {
          width: 100%;
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 14px 20px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
          margin-bottom: 24px;
        }

        .login-btn:hover:not(:disabled) {
          background-color: #3367d6;
        }

        .login-btn:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }

        .back-link {
          text-align: center;
        }

        .back-link button {
          color: #666;
          background: none;
          border: none;
          font-size: 14px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: color 0.2s ease;
          cursor: pointer;
        }

        .back-link button:hover {
          color: #4285f4;
        }

        .back-arrow {
          font-size: 12px;
        }

        .message {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 16px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          font-size: 14px;
          font-weight: 500;
        }

        .message.error {
          background: #fee;
          border: 1px solid #fcc;
          color: #c33;
        }

        .message.success {
          background: #efe;
          border: 1px solid #cfc;
          color: #3c3;
        }

        @media (max-width: 480px) {
          .login-container {
            padding: 30px 20px;
            margin: 10px;
          }
          
          .header h1 {
            font-size: 24px;
            font-weight: bold !important;
            color: #000000 !important;
          }
        }
      </style>

      <div class="login-body">
        <div class="login-container">
          <div class="header">
            <h1>Bem-vindo de volta</h1>
            <p>Faça login para continuar construindo os seus projetos.</p>
          </div>

          <div class="tab-container">
            <button class="tab active" data-tab="login">Fazer login</button>
            <button class="tab" data-tab="register">Criar conta</button>
          </div>

          <form id="loginForm">
            <div class="form-group">
              <label for="nif">NIF</label>
              <input 
                type="text" 
                id="nif" 
                name="nif" 
                placeholder="Digite o seu NIF (9 dígitos)"
                pattern="[0-9]{9}"
                maxlength="9"
                required
              />
            </div>

            <div class="form-group">
              <label for="password">Palavra-passe</label>
              <input 
                type="password" 
                id="password" 
                name="password" 
                placeholder="Digite a sua palavra-passe"
                required
              />
            </div>

            <button type="submit" class="login-btn" id="submitBtn">
              Fazer login
            </button>
          </form>

          <div class="back-link">
            <button type="button" id="backLink">
              <span class="back-arrow">←</span>
              Voltar ao início
            </button>
          </div>
        </div>
      </div>
    `

    this.setupEventListeners()
    return this.container
  }

  private setupEventListeners(): void {
    if (!this.container) return

    const form = this.container.querySelector('#loginForm') as HTMLFormElement
    const backLink = this.container.querySelector('#backLink') as HTMLButtonElement
    const registerTab = this.container.querySelector('[data-tab="register"]') as HTMLButtonElement

    // Form inputs
    const nifInput = this.container.querySelector('#nif') as HTMLInputElement
    const passwordInput = this.container.querySelector('#password') as HTMLInputElement

    // Form validation and submission
    form.addEventListener('submit', this.handleSubmit.bind(this))

    // Input validation
    nifInput.addEventListener('input', (e) => {
      this.nif = (e.target as HTMLInputElement).value
      this.validateForm()
    })

    passwordInput.addEventListener('input', (e) => {
      this.password = (e.target as HTMLInputElement).value
      this.validateForm()
    })

    // Handle Enter key navigation
    nifInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        passwordInput.focus()
      }
    })

    passwordInput.addEventListener('keypress', async (e) => {
      if (e.key === 'Enter') {
        await this.handleSubmit(e)
      }
    })

    // Tab navigation
    registerTab.addEventListener('click', () => {
      router.navigate('/register')
    })

    // Back button
    backLink.addEventListener('click', () => {
      router.navigate('/')
    })

    // Initial validation
    this.validateForm()
  }

  private validateForm(): void {
    if (!this.container) return

    const submitBtn = this.container.querySelector('#submitBtn') as HTMLButtonElement
    const isValid = this.isValidNIF(this.nif) && this.password.length >= 6

    submitBtn.disabled = !isValid
  }

  private isValidNIF(nif: string): boolean {
    if (!nif || nif.length !== 9) return false
    
    // Portuguese NIF validation algorithm
    const digits = nif.split('').map(Number)
    const checkDigit = digits[8]
    
    let sum = 0
    for (let i = 0; i < 8; i++) {
      sum += digits[i] * (9 - i)
    }
    
    const remainder = sum % 11
    const expectedCheckDigit = remainder < 2 ? 0 : 11 - remainder
    
    return checkDigit === expectedCheckDigit
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault()

    if (!this.nif || !this.password) {
      this.showMessage('Por favor, preencha todos os campos', 'error')
      return
    }

    if (!this.isValidNIF(this.nif)) {
      this.showMessage('Por favor, digite um NIF válido', 'error')
      return
    }

    if (this.password.length < 6) {
      this.showMessage('A palavra-passe deve ter pelo menos 6 caracteres', 'error')
      return
    }

    const submitBtn = this.container?.querySelector('#submitBtn') as HTMLButtonElement

    // Show loading state
    submitBtn.disabled = true
    submitBtn.textContent = 'A fazer login...'

    try {
      // Backend expects NIF + password for Portuguese authentication
      const response = await api.post('/api/auth/login', {
        nif: this.nif,
        password: this.password,
      })

      this.storeAuthData(response)
      this.showMessage('Login efetuado com sucesso!', 'success')
      
      setTimeout(() => {
        // Check if there's a subscription intent or return URL
        const urlParams = new URLSearchParams(window.location.search)
        const returnUrl = urlParams.get('return')
        const action = urlParams.get('action')
        
        if (returnUrl === 'pricing' && action === 'subscribe') {
          router.navigate('/pricing')
        } else {
          router.navigate('/dashboard')
        }
      }, 1000)
    } catch (error) {
      console.error('Login error:', error)
      this.showMessage('NIF ou palavra-passe incorretos', 'error')
    } finally {
      // Hide loading state
      submitBtn.disabled = false
      submitBtn.textContent = 'Fazer login'
    }
  }

  private storeAuthData(data: any): void {
    if (data.token) {
      localStorage.setItem('auth_token', data.token)
    }

    if (data.user) {
      localStorage.setItem('user_data', JSON.stringify(data.user))
    }

    localStorage.setItem('login_timestamp', new Date().toISOString())
  }

  private showMessage(message: string, type: 'success' | 'error'): void {
    // Remove any existing message
    const existingMessage = document.querySelector('.message')
    if (existingMessage) {
      existingMessage.remove()
    }

    const messageElement = document.createElement('div')
    messageElement.className = `message ${type}`
    messageElement.textContent = message

    document.body.appendChild(messageElement)

    setTimeout(() => {
      messageElement.remove()
    }, 5000)
  }
}
